import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { TemplateVariablePicker } from "components/TemplateVariablePicker/TemplateVariablePicker";
import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "../../../shared/templateModal.module.css";

const isEditorEmpty = (value) =>
  !value || value.replace(/<[^>]*>/g, "").trim().length === 0;

const emailTemplateSchema = z.object({
  name: z.string().min(1, "Please enter template name."),
  subject: z.string().min(1, "Please enter email subject."),
  template: z
    .string()
    .refine((val) => !isEditorEmpty(val), "Please enter template content."),
});

const DEFAULT_VALUES = {
  name: "",
  subject: "",
  template: "",
};

export const EmailTemplateModal = ({
  isOpen,
  onClose,
  onSubmit,
  editTemplate,
}) => {
  const methods = useForm({
    resolver: zodResolver(emailTemplateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { control, handleSubmit, reset, getValues, setValue } = methods;

  const handleInsertVariable = (placeholder) => {
    const current = getValues("template") || "";
    setValue("template", current + placeholder, { shouldDirty: true });
  };

  const handleInsertSubjectVariable = (placeholder) => {
    const current = getValues("subject") || "";
    setValue("subject", current + placeholder, { shouldDirty: true });
  };

  useEffect(() => {
    if (!isOpen) return;

    reset(
      editTemplate
        ? {
            name: editTemplate.name ?? "",
            subject: editTemplate.subject ?? "",
            template: editTemplate.template ?? "",
          }
        : DEFAULT_VALUES
    );
  }, [isOpen, editTemplate, reset]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={editTemplate ? "Edit Email Template" : "Add New Email Template"}
      width={900}
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("EmailTemplateModal"))}>
          <div className={styles.fields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Template Name</label>
              <FormField name="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Template Name"
                      className={styles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>Email Subject</label>
                <TemplateVariablePicker onInsert={handleInsertSubjectVariable} />
              </div>
              <FormField name="subject">
                <Controller
                  name="subject"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Email Subject"
                      className={styles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>Template Content</label>
                <TemplateVariablePicker onInsert={handleInsertVariable} />
              </div>
              <FormField name="template">
                <Controller
                  name="template"
                  control={control}
                  render={({ field }) => (
                    <RichEditor
                      editorValue={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormField>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="medium"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  );
};
